<template>
  <div>
    <v-row noGutters>
      <v-col>
        <Breadcrumbs :items="breadcrumbs" />
      </v-col>
    </v-row>
    <v-row noGutters>
      <BaseSidebar
        :items="computedSidebarItems"
        :title="$t('administration.pageName')"
        :hasCategories="true"
      />
      <v-col>
        <RouterViewPage />
        <v-overlay :value="isLoading">
          <v-progress-circular
            indeterminate
            size="64"
            color="blue"
          />
        </v-overlay>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import Breadcrumbs from '@/components/shared/navigation/Breadcrumbs';
import BaseSidebar from '@/components/shared/navigation/BaseSidebar';
import { TYPE_GENAI } from '@/aquassist-shared/constants/types/features';
import { isFeatureActive } from '@/utilities/featureValidator';
import RouterViewPage from '@/views/RouterViewPage.vue';
import roleConstants from '../../utilities/enums/rolesTypes';


export default {
    components: {
        BaseSidebar,
        Breadcrumbs,
        RouterViewPage,
    },
    data() {
        return {
            isChatActive: false,
        };
    },
    computed: {
        ...mapGetters([
            'breadcrumbs',
            'hasRole',
            'userLang',
            'superAdmin',
            'teamID',
            'showContractByFeature',
        ]),
        ...(() => ({
            ...mapState({
                isLoading: state => state.loading.loading,
            }),
            ...mapState('dynamicRoutes', {
                chatRoutes: state => state.chatRoutes,
            }),
        }))(),

        sidebarItems() {
            const sideBar = this.hasRole(roleConstants.FINANCIALADMINISTRATOR) ? [] : [
                {
                    title: 'administration.management',
                    icon: 'ManagementAdmin.svg',
                    categoryItems: [
                        {
                            title: 'administration.users',
                            path: { name: 'users' },
                        },
                        {
                            title: 'administration.accessGroups',
                            path: { name: 'accessGroups' },
                        },
                        {
                            title: 'administration.automaticSentences',
                            path: { name: 'automaticSentences' },
                        },
                        {
                            title: 'administration.defaultAnalyses',
                            path: { name: 'defaultAnalyses' },
                        },
                        {
                            title: 'administration.standardFamilies',
                            path: { name: 'standardFamilies' },
                        },
                        {
                            title: 'administration.features',
                            path: { name: 'mfeatures' },
                        },
                    ],
                },
                {
                    title: 'administration.configuration',
                    icon: 'AdminConfig.svg',
                    categoryItems: [
                        {
                            title: 'administration.analyses',
                            // path: { name: 'analyses' },
                            subCateggoryItems: [
                                {
                                    title: 'administration.analysesCategories',
                                    path: { name: 'analysesCategories' },
                                },
                                {
                                    title: 'administration.analysesTypes',
                                    path: { name: 'analysesTypes' },
                                },
                                {
                                    title: 'administration.analysesGroups',
                                    path: { name: 'analysesGroups' },
                                },
                                {
                                    title: 'administration.correctiveActions',
                                    path: { name: 'correctiveActions' },
                                },
                            ],
                        },
                        {
                            title: 'administration.coupons',
                            path: { name: 'coupons' },
                        },
                        {
                            title: 'administration.maintenanceSchedule',
                            path: { name: 'adminMaintenanceSchedule' },
                        },
                        {
                            title: 'administration.procedures',
                            path: { name: 'procedures' },
                        },
                        {
                            title: 'administration.products',
                            path: { name: 'products' },
                        },
                        {
                            title: 'administration.features',
                            subCateggoryItems: [
                                {
                                    title: 'administration.erp',
                                    path: { name: 'erp' },
                                },
                            ],
                        },
                        {
                            title: 'administration.productsCategories',
                            path: { name: 'productsCategories' },
                        },
                        {
                            title: 'treatmentControls.pageName',
                            path: { name: 'treatmentControls' },
                        },
                        {
                            title: 'documents.types.pageName',
                            path: { name: 'documentTypes' },
                        },
                        {
                            title: 'administration.productContainer',
                            path: { name: 'productContainer' },
                        },
                        {
                            title: 'administration.serviceProvider',
                            path: { name: 'serviceProviderInfo' },
                        },

                    ],
                },
            ];
            if (this.isChatActive && !this.hasRole(roleConstants.FINANCIALADMINISTRATOR)) {
                sideBar[sideBar.length - 1].categoryItems.push({
                    title: 'administration.gptconfig',
                    path: { name: 'gptconfig' },
                });
            }

            if (this.hasRole(roleConstants.ADMINISTRATOR)) {
                sideBar.push({
                    title: 'administration.advancedConfiguration',
                    icon: 'AdminConfig.svg',
                    categoryItems: [
                        {
                            title: 'administration.serviceProviders',
                            path: { name: 'serviceProviders' },
                        },
                        {
                            title: 'administration.nodes',
                            path: { name: 'nodes' },
                        },
                        {
                            title: 'administration.rolesPrivileges',
                        },
                        {
                            title: 'administration.videoCategories',
                            path: { name: 'videoCategories' },
                        },
                        {
                            title: 'administration.devices',
                            path: { name: 'devices' },
                        },
                        {
                            title: 'administration.deviceAnalysisKeys',
                            path: { name: 'deviceAnalysisKeys' },
                        },
                        {
                            title: 'reports.types.pageName',
                            path: { name: 'reportTypes' },
                        },
                        {
                            title: 'administration.analysisTypeTypes',
                            path: { name: 'analysisTypeTypes' },
                        },
                        {
                            title: 'administration.workOrderTypes',
                            path: { name: 'workOrderTypes' },
                        },
                        {
                            title: 'administration.features',
                            path: { name: 'features' },
                        },
                        {
                            title: 'administration.release',
                            path: { name: 'release' },
                        },
                        {
                            title: 'administration.serviceProviderBilling',
                            path: { name: 'serviceProviderBilling' },
                        },
                        {
                            title: 'administration.logs',
                            path: { name: 'logs' },
                        },
                        {
                            title: 'administration.analysisParamsUpload',
                            path: { name: 'analysisParamsUpload' },
                        },
                    ],
                });
                if (this.isChatActive) {
                    sideBar[sideBar.length - 1].categoryItems.push({
                        title: 'administration.gptconfig',
                        path: { name: 'publicGptConfig' },
                    });
                }
            }
            if (this.superAdmin) {
                const advancedConfiguration = sideBar.find(item => item.title === 'administration.advancedConfiguration');
                if (advancedConfiguration) {
                    advancedConfiguration.categoryItems.push({
                        title: 'administration.termsOfUse',
                        path: { name: 'termsOfUse' },
                    });
                }
            }
            if (this.hasRole(roleConstants.ADMINISTRATOR) || this.hasRole(roleConstants.MANAGER)) {
                const management = sideBar.find(item => item.title === 'administration.management');
                if (management) {
                    management.categoryItems.push({
                        title: 'administration.billingRegister',
                        path: { name: 'managementBillingRegister' },
                    });
                }
            }
            if (this.showContractByFeature) {
                const management = sideBar.find(item => item.title === 'administration.management');
                if (management) {
                    management.categoryItems.push({
                        title: 'administration.contracts',
                        path: { name: 'contracts' },
                    });
                }
            }
            if (this.hasRole(roleConstants.FINANCIALADMINISTRATOR) || this.superAdmin) {
                sideBar.push({
                    title: 'administration.aquassistBilling',
                    icon: 'AquassistBilling.svg',
                    categoryItems: [
                        {
                            title: 'administration.pricingManagement',
                            path: { name: 'pricingManagement' },
                        },
                        {
                            title: 'administration.billingRegister',
                            path: { name: 'billingRegister' },
                        },
                    ],
                });
            }
            return sideBar;
        },
        computedSidebarItems() {
            const baseItems = this.sidebarItems || [];
            const chatItemGroup = this.chatRoutes && this.chatRoutes.length && this.chatRoutes.length > 0
                ? {
                    title: 'administration.chatOptions',
                    icon: 'Chats.svg',
                    categoryItems: this.chatRoutes.map(chatRoute => ({
                        title: chatRoute.title,
                        path: chatRoute.path,
                    })),
                }
                : null;
            return chatItemGroup ? [...baseItems, chatItemGroup] : baseItems;
        },
    },
    watch: {
        $route() {
            this.buildFromRoute();
        },
    },
    async created() {
        await this.buildFromRoute();
    },
    methods: {
        ...mapActions({
            emptyBreadcrumbs: 'emptyBreadcrumbs',
            pushBreadcrumb: 'pushBreadcrumb',
            reloadTeam: 'reloadTeam',
        }),
        async buildFromRoute() {
            this.reloadTeam(this.teamID);
            const { data } = await this.$rq.getFeatures({});
            this.isChatActive = isFeatureActive(data, TYPE_GENAI, this.teamID);
            this.emptyBreadcrumbs();

            const currentPath = this.$route.matched[this.$route.matched.length - 1].path.split('/');
            currentPath.shift();

            currentPath.forEach(route => {
                switch (route) {
                    case 'management':
                    case 'configuration':
                    case 'advancedConfiguration':
                    case 'aquassistBilling':
                    case 'trends':
                    case 'documentLibrary':
                    case 'networkTypes':
                    case 'componentTypes':
                    case 'analysisTypes':
                        this.pushBreadcrumb({
                            text: `administration.${route}`,
                            disabled: true,
                        });
                        break;
                    case ':networkTypeId':
                        this.pushBreadcrumb({
                            text: this.$store.getters.networkType.name || this.$t('forms.labels.any'),
                            noTranslate: true,
                            disabled: true,
                        });
                        break;
                    case ':componentTypeId':
                        this.pushBreadcrumb({
                            text: this.$store.getters.componentType.name[this.userLang] || this.$t('forms.labels.any'),
                            noTranslate: true,
                            disabled: true,
                        });
                        break;
                    case ':standardFamilyId':
                        this.pushBreadcrumb({
                            name: 'standards',
                            text: this.$store.getters.standardFamily.name,
                            noTranslate: true,
                        });
                        break;
                    case ':productId':
                        this.pushBreadcrumb({
                            name: 'product',
                            text: this.$store.getters.product.name,
                            noTranslate: true,
                        });
                        break;
                    case ':analysisGroupId':
                        this.pushBreadcrumb({
                            name: 'analysisGroup',
                            // FIXME: need to be displayed in user's language
                            text: this.$store.getters.analysisGroup.name_en,
                            noTranslate: true,
                        });
                        break;
                    case ':memberId':
                        this.pushBreadcrumb({
                            name: 'user',
                            text: `${this.$store.getters.member.firstName} ${this.$store.getters.member.lastName}`,
                            noTranslate: true,
                        });
                        break;
                    case ':accessGroupId':
                        this.pushBreadcrumb({
                            name: 'accessGroup',
                            text: this.$store.getters.accessGroup.name_en,
                            noTranslate: true,
                        });
                        break;
                        // Disable click for the 'info' path
                    case 'info':
                        this.pushBreadcrumb({
                            text: `administration.${route}`,
                            disabled: true,
                        });
                        break;
                    default:
                        this.pushBreadcrumb({
                            name: route,
                            text: `administration.${route}`,
                        });
                }
            });
        },
    },
};
</script>
